import React from 'react';
import { motion } from 'framer-motion';
interface Player {
  id: string;
  name: string;
  color: string;
  pathIndex: number | null;
  avatar?: string | null;
}
interface JourneyStartSceneProps {
  players: Player[];
  onStartPlaying: () => void;
}
const PATH_COLORS = [{
  stroke: '#8b5cf6',
  fill: '#a78bfa',
  name: 'School',
  emoji: '🎓'
}, {
  stroke: '#ec4899',
  fill: '#f472b6',
  name: 'Workforce',
  emoji: '💼'
}, {
  stroke: '#f59e0b',
  fill: '#fbbf24',
  name: 'Trade',
  emoji: '🔧'
}, {
  stroke: '#14b8a6',
  fill: '#2dd4bf',
  name: 'Self-Made',
  emoji: '✨'
} // teal
];
export function JourneyStartScene({
  players,
  onStartPlaying
}: JourneyStartSceneProps) {
  // Group players by their chosen path so pawns spread out at the start
  const playersByPath: Record<number, Player[]> = {
    0: [],
    1: [],
    2: [],
    3: []
  };
  players.forEach(p => {
    const idx = p.pathIndex !== null && p.pathIndex !== undefined ? Math.max(0, Math.min(3, p.pathIndex)) : 0;
    playersByPath[idx].push(p);
  });
  // Path geometry: each path starts near center bottom and curves to top
  const paths = ['M 200 520 Q 120 380 80 220 T 60 40', 'M 320 520 Q 280 380 240 220 T 220 40', 'M 480 520 Q 520 380 560 220 T 580 40', 'M 600 520 Q 680 380 720 220 T 740 40'];
  return <div className="fixed inset-0 w-full h-full z-30 overflow-hidden" style={{
    background: 'linear-gradient(to bottom, #2a0f5c 0%, #6b21a8 30%, #db2777 60%, #f97316 85%, #fbbf24 100%)'
  }} data-id="element-194">
      {/* Sun */}
      <div className="absolute" style={{
      top: '22%',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '180px',
      height: '180px',
      borderRadius: '50%',
      background: 'radial-gradient(circle, #fef3c7 0%, #fbbf24 40%, #f97316 70%, transparent 100%)',
      filter: 'blur(2px)'
    }} data-id="element-195" />

      {/* Twinkling stars */}
      {[...Array(40)].map((_, i) => <motion.div key={i} className="absolute bg-white rounded-full" style={{
      width: `${1 + Math.random() * 2}px`,
      height: `${1 + Math.random() * 2}px`,
      top: `${Math.random() * 35}%`,
      left: `${Math.random() * 100}%`
    }} animate={{
      opacity: [0.3, 1, 0.3]
    }} transition={{
      duration: 2 + Math.random() * 3,
      repeat: Infinity,
      delay: Math.random() * 2
    }} data-id="element-196" />)}

      {/* Distant mountain silhouette */}
      <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 800 200" preserveAspectRatio="none" style={{
      height: '30%',
      opacity: 0.5
    }} data-id="element-197">
        <path d="M0,200 L0,140 L80,80 L160,120 L240,60 L320,100 L400,40 L480,90 L560,50 L640,100 L720,70 L800,120 L800,200 Z" fill="#1e1b4b" data-id="element-198" />
      </svg>

      {/* Ground glow */}
      <div className="absolute bottom-0 left-0 right-0" style={{
      height: '40%',
      background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)'
    }} data-id="element-199" />

      {/* Paths SVG */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet" data-id="element-200">
        <defs data-id="element-201">
          {PATH_COLORS.map((c, i) => <linearGradient key={i} id={`pathGrad${i}`} x1="0%" y1="100%" x2="0%" y2="0%" data-id="element-202">
              <stop offset="0%" stopColor={c.fill} stopOpacity="0.95" data-id="element-203" />
              <stop offset="100%" stopColor={c.stroke} stopOpacity="0.3" data-id="element-204" />
            </linearGradient>)}
        </defs>

        {paths.map((d, i) => <g key={i} data-id="element-205">
            {/* Outer glow */}
            <path d={d} fill="none" stroke={PATH_COLORS[i].stroke} strokeWidth="38" strokeLinecap="round" opacity="0.25" data-id="element-206" />
            {/* Main path */}
            <path d={d} fill="none" stroke={`url(#pathGrad${i})`} strokeWidth="22" strokeLinecap="round" data-id="element-207" />
            {/* Center dashed line */}
            <motion.path d={d} fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeDasharray="8 12" opacity="0.7" initial={{
          strokeDashoffset: 0
        }} animate={{
          strokeDashoffset: -40
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-208" />
          </g>)}
      </svg>

      {/* Player pawns at path starts */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-209">
        {[0, 1, 2, 3].map(pathIdx => {
        const startPositions = [{
          left: '25%',
          bottom: '8%'
        }, {
          left: '40%',
          bottom: '8%'
        }, {
          left: '60%',
          bottom: '8%'
        }, {
          left: '75%',
          bottom: '8%'
        }];
        const pos = startPositions[pathIdx];
        const pathPlayers = playersByPath[pathIdx];
        const pathColor = PATH_COLORS[pathIdx];
        return <div key={pathIdx} className="absolute flex flex-col items-center gap-1" style={{
          left: pos.left,
          bottom: pos.bottom,
          transform: 'translateX(-50%)'
        }} data-id="element-210">
              <div className="flex gap-1 mb-1 flex-wrap justify-center max-w-[140px]" data-id="element-211">
                {pathPlayers.map((p, idx) => <motion.div key={p.id} initial={{
              y: -20,
              opacity: 0
            }} animate={{
              y: [0, -6, 0],
              opacity: 1
            }} transition={{
              y: {
                duration: 1.8,
                repeat: Infinity,
                delay: idx * 0.2
              },
              opacity: {
                duration: 0.4,
                delay: idx * 0.1
              }
            }} className="flex flex-col items-center" data-id="element-212">
                    <div className="rounded-full border-[3px] border-white shadow-2xl overflow-hidden flex items-center justify-center" style={{
                width: 44,
                height: 44,
                background: pathColor.fill,
                boxShadow: `0 0 20px ${pathColor.stroke}`
              }} data-id="element-213">
                      {p.avatar ? <img src={p.avatar} alt="" className="w-full h-full object-cover" data-id="element-214" /> : <span className="text-white font-bold text-lg" data-id="element-215">
                          {p.name.charAt(0).toUpperCase()}
                        </span>}
                    </div>
                    <div className="mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white whitespace-nowrap" style={{
                background: pathColor.stroke
              }} data-id="element-216">
                      {p.name}
                    </div>
                  </motion.div>)}
              </div>
              {/* Path label */}
              <div className="px-3 py-1 rounded-full text-xs font-bold text-white shadow-lg" style={{
            background: pathColor.stroke
          }} data-id="element-217">
                {pathColor.emoji} {pathColor.name}
              </div>
            </div>;
      })}
      </div>

      {/* Floating dice */}
      <motion.div className="absolute" style={{
      top: '30%',
      right: '8%'
    }} animate={{
      y: [0, -15, 0],
      rotate: [0, 10, -10, 0]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-218">
        <div className="text-7xl drop-shadow-2xl" data-id="element-219">🎲</div>
      </motion.div>

      {/* Top title */}
      <div className="absolute top-0 left-0 right-0 flex flex-col items-center pt-8 pb-4 bg-gradient-to-b from-black/50 via-black/10 to-transparent pointer-events-none z-10" data-id="element-220">
        <div className="flex items-center gap-3 mb-2" data-id="element-221">
          <span className="text-3xl" data-id="element-222">✨</span>
          <h1 className="text-4xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-pink-300 to-purple-400 drop-shadow-lg text-center" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-223">
            Your Journey Begins!
          </h1>
          <span className="text-3xl" data-id="element-224">✨</span>
        </div>
        <p className="text-xs md:text-sm text-white/90 font-bold tracking-widest uppercase bg-black/30 px-6 py-1.5 rounded-full backdrop-blur-sm text-center" data-id="element-225">
          Follow your path. Make your choices. Build your life.
        </p>
      </div>

      {/* Roll Dice CTA */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center z-20" data-id="element-226">
        <motion.button onClick={onStartPlaying} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.97
      }} className="group flex items-center gap-4 bg-indigo-900/90 backdrop-blur-md p-2 pr-8 rounded-full border-2 border-indigo-400 shadow-[0_0_40px_rgba(79,70,229,0.6)] hover:bg-indigo-800 transition-colors" data-id="element-227">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-inner" data-id="element-228">
            <motion.span className="text-4xl" animate={{
            rotate: [0, 360]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-229">
              🎲
            </motion.span>
          </div>
          <div className="flex flex-col items-start" data-id="element-230">
            <span className="text-white font-bold text-lg uppercase tracking-wider" data-id="element-231">
              Roll the dice
            </span>
            <span className="text-indigo-200 text-sm" data-id="element-232">
              to start your journey!
            </span>
          </div>
        </motion.button>
      </div>
    </div>;
}